import { AgentHistoryService } from '../services/agentHistoryService'
import { AGENT_TYPES } from '../constants/agentConfig.js'

/**
 * Utility functions untuk integrasi agent dengan history service
 */
export class AgentHistoryIntegration {
  /**
   * Map frontend agent_type to database agent_type
   * @param {string} frontendAgentType - Frontend agent type
   * @returns {string} Database agent type
   */
  static mapAgentTypeToDatabase(frontendAgentType) {
    // Database now uses '_agent' format consistently
    // Keep all agent types with '_agent' suffix to match database schema
    const mapping = {
      'research_agent': 'research_agent',
      'skripsi_agent': 'skripsi_agent',
      'artikel_agent': 'artikel_agent', 
      'makalah_agent': 'makalah_agent',
      'gambar_agent': 'gambar_agent',
      'video_agent': 'video_agent',
      'berita_agent': 'berita_agent',
      'buku_agent': 'buku_agent',
      'novel_agent': 'novel_agent',
      'search_agent': 'search_agent', // Fixed: keep as search_agent
      'trending_agent': 'trending_agent',
      'writing_agent': 'writing_agent',
      'ppt_agent': 'ppt_agent',
      'chart_agent': 'chart_agent',
      'pdf_agent': 'pdf_agent'
    }
    
    return mapping[frontendAgentType] || frontendAgentType
  }

  /**
   * Menyimpan hasil agent ke dalam history
   * @param {Object} agentData - Data agent yang akan disimpan
   * @param {string} agentData.query - Query asli
   * @param {string} agentData.agentType - Tipe agent (skripsi, artikel, makalah, gambar)
   * @param {Object} agentData.content - Konten hasil agent
   * @param {Array} agentData.sources - Sumber referensi
   * @param {Object} agentData.metadata - Metadata tambahan
   * @param {string} agentData.title - Judul (opsional)
   * @param {string} agentData.status - Status (default: 'completed')
   * @param {string} agentData.historyId - ID history untuk update (opsional)
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveAgentResult(agentData) {
    try {
      // Convert frontend agent_type to database agent_type
      const databaseAgentType = this.mapAgentTypeToDatabase(agentData.agentType)
      
      // Jika historyId ada, lakukan update
      if (agentData.historyId) {
        const result = await AgentHistoryService.updateAgentResult(agentData.historyId, {
          title: agentData.title || this.generateTitle(agentData.query, agentData.agentType),
          content: agentData.content,
          sources: agentData.sources || [],
          metadata: agentData.metadata || {},
          status: agentData.status || 'completed'
        })
        
        if (result.error) {
          console.error('Error updating agent result in history:', result.error)
          return { success: false, error: result.error }
        }
        
        console.log('Agent result updated in history successfully:', result.data)
        return { success: true, data: result.data }
      }
      
      // Jika tidak ada historyId, buat record baru
      const result = await AgentHistoryService.saveAgentResult({
        title: agentData.title || this.generateTitle(agentData.query, agentData.agentType),
        query: agentData.query,
        agentType: databaseAgentType, // Use mapped database agent_type
        agentId: agentData.agentId || agentData.agentType,
        content: agentData.content,
        sources: agentData.sources || [],
        metadata: agentData.metadata || {},
        status: agentData.status || 'completed'
      })
      
      if (result.error) {
        console.error('Error saving agent result to history:', result.error)
        return { success: false, error: result.error }
      }
      
      console.log('Agent result saved to history successfully:', result.data)
      return { success: true, data: result.data }
    } catch (error) {
      console.error('Error in saveAgentResult:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Menyimpan hasil Skripsi Agent
   * @param {Object} skripsiData - Data skripsi
   * @param {string} skripsiData.query - Query asli
   * @param {Array} skripsiData.outline - Outline skripsi
   * @param {Object} skripsiData.generatedContent - Konten yang dihasilkan
   * @param {Array} skripsiData.academicSources - Sumber akademik
   * @param {Object} skripsiData.progress - Progress data
   * @param {string} skripsiData.status - Status ('in_progress', 'completed')
   * @param {string} skripsiData.historyId - ID history untuk update (opsional)
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveSkripsiResult(skripsiData) {
    const content = {
      outline: skripsiData.outline,
      generatedContent: skripsiData.generatedContent,
      fullText: this.combineSkripsiContent(skripsiData.outline, skripsiData.generatedContent),
      wordCount: this.calculateWordCount(skripsiData.generatedContent)
    }

    const metadata = {
      progress: skripsiData.progress,
      totalSections: skripsiData.outline?.length || 0,
      completedSections: Object.keys(skripsiData.generatedContent || {}).length,
      generatedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: skripsiData.query,
      agentType: AGENT_TYPES.SKRIPSI,
      content: content,
      sources: skripsiData.academicSources || [],
      metadata: metadata,
      status: skripsiData.status || 'completed',
      historyId: skripsiData.historyId
    })
  }

  /**
   * Menyimpan outline Skripsi (tahap awal)
   * @param {Object} outlineData - Data outline
   * @param {string} outlineData.query - Query asli
   * @param {Array} outlineData.outline - Outline skripsi
   * @param {Array} outlineData.academicSources - Sumber akademik
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveSkripsiOutline(outlineData) {
    const content = {
      outline: outlineData.outline,
      generatedContent: {},
      fullText: '',
      wordCount: 0
    }

    const metadata = {
      progress: { current: 0, total: outlineData.outline?.length || 0 },
      totalSections: outlineData.outline?.length || 0,
      completedSections: 0,
      generatedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      stage: 'outline_created'
    }

    return this.saveAgentResult({
      query: outlineData.query,
      agentType: AGENT_TYPES.SKRIPSI,
      content: content,
      sources: outlineData.academicSources || [],
      metadata: metadata,
      status: 'in_progress'
    })
  }

  /**
   * Update progress Skripsi (per section)
   * @param {Object} progressData - Data progress
   * @param {string} progressData.historyId - ID history untuk update
   * @param {string} progressData.query - Query asli
   * @param {Array} progressData.outline - Outline skripsi
   * @param {Object} progressData.generatedContent - Konten yang dihasilkan
   * @param {Array} progressData.academicSources - Sumber akademik
   * @param {Object} progressData.progress - Progress data
   * @param {string} progressData.status - Status ('in_progress', 'completed')
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async updateSkripsiProgress(progressData) {
    return this.saveSkripsiResult({
      ...progressData,
      status: progressData.status || 'in_progress'
    })
  }

  /**
   * Menyimpan hasil Artikel Agent
   * @param {Object} artikelData - Data artikel
   * @param {string} artikelData.query - Query asli
   * @param {Array} artikelData.outline - Outline artikel
   * @param {Object} artikelData.generatedContent - Konten yang dihasilkan
   * @param {Array} artikelData.academicSources - Sumber akademik
   * @param {Object} artikelData.progress - Progress data
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveArtikelResult(artikelData) {
    const content = {
      outline: artikelData.outline,
      generatedContent: artikelData.generatedContent,
      fullText: this.combineArtikelContent(artikelData.outline, artikelData.generatedContent),
      wordCount: this.calculateWordCount(artikelData.generatedContent)
    }

    const metadata = {
      progress: artikelData.progress,
      totalSections: artikelData.outline?.length || 0,
      completedSections: Object.keys(artikelData.generatedContent || {}).length,
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: artikelData.query,
      agentType: AGENT_TYPES.ARTIKEL,
      content: content,
      sources: artikelData.academicSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Makalah Agent
   * @param {Object} makalahData - Data makalah
   * @param {string} makalahData.query - Query asli
   * @param {Array} makalahData.outline - Outline makalah
   * @param {Object} makalahData.generatedContent - Konten yang dihasilkan
   * @param {Array} makalahData.academicSources - Sumber akademik
   * @param {Object} makalahData.progress - Progress data
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveMakalahResult(makalahData) {
    const content = {
      outline: makalahData.outline,
      generatedContent: makalahData.generatedContent,
      fullText: this.combineMakalahContent(makalahData.outline, makalahData.generatedContent),
      wordCount: this.calculateWordCount(makalahData.generatedContent)
    }

    const metadata = {
      progress: makalahData.progress,
      totalSections: makalahData.outline?.length || 0,
      completedSections: Object.keys(makalahData.generatedContent || {}).length,
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: makalahData.query,
      agentType: AGENT_TYPES.MAKALAH,
      content: content,
      sources: makalahData.academicSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Gambar Agent
   * @param {Object} gambarData - Data gambar
   * @param {string} gambarData.query - Query asli
   * @param {Array} gambarData.images - Array gambar yang dihasilkan
   * @param {Object} gambarData.settings - Settings yang digunakan
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveGambarResult(gambarData) {
    const content = {
      images: gambarData.images || [],
      totalImages: gambarData.images?.length || 0,
      settings: gambarData.settings || {}
    }

    const metadata = {
      imageCount: gambarData.images?.length || 0,
      settings: gambarData.settings || {},
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: gambarData.query,
      agentType: AGENT_TYPES.GAMBAR,
      content: content,
      sources: [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Berita Agent
   * @param {Object} beritaData - Data berita
   * @param {string} beritaData.query - Query asli
   * @param {Object} beritaData.selectedOption - Opsi berita yang dipilih
   * @param {string} beritaData.generatedContent - Konten berita yang dihasilkan
   * @param {Array} beritaData.newsSources - Sumber berita
   * @param {string} beritaData.language - Bahasa berita (id/en)
   * @param {Object} beritaData.progress - Progress generation
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveBeritaResult(beritaData) {
    const content = {
      selectedOption: beritaData.selectedOption,
      generatedContent: beritaData.generatedContent,
      fullText: beritaData.generatedContent,
      wordCount: this.calculateBeritaWordCount(beritaData.generatedContent),
      language: beritaData.language
    }

    const metadata = {
      progress: beritaData.progress,
      selectedOption: beritaData.selectedOption,
      language: beritaData.language,
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: beritaData.query,
      agentType: AGENT_TYPES.BERITA,
      content: content,
      sources: beritaData.newsSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Buku Agent
   * @param {Object} bukuData - Data buku
   * @param {string} bukuData.query - Query asli
   * @param {Array} bukuData.outline - Outline buku
   * @param {Object} bukuData.generatedContent - Konten yang dihasilkan
   * @param {Array} bukuData.academicSources - Sumber akademik
   * @param {Object} bukuData.progress - Progress data
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveBukuResult(bukuData) {
    const content = {
      outline: bukuData.outline,
      generatedContent: bukuData.generatedContent,
      fullText: this.combineBukuContent(bukuData.outline, bukuData.generatedContent),
      wordCount: this.calculateWordCount(bukuData.generatedContent)
    }

    const metadata = {
      progress: bukuData.progress,
      totalSections: bukuData.outline?.length || 0,
      completedSections: Object.keys(bukuData.generatedContent || {}).length,
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: bukuData.query,
      agentType: AGENT_TYPES.BUKU,
      content: content,
      sources: bukuData.academicSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Novel Agent
   * @param {Object} novelData - Data novel
   * @param {string} novelData.query - Query asli
   * @param {Array} novelData.outline - Outline novel
   * @param {Object} novelData.generatedContent - Konten yang dihasilkan
   * @param {Object} novelData.progress - Progress data
   * @param {string} novelData.status - Status ('in_progress', 'completed')
   * @param {string} novelData.historyId - ID history untuk update (opsional)
   * @param {Object} novelData.novelConfig - Konfigurasi novel (genre, style, plotStructure, language)
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveNovelResult(novelData) {
    const content = {
      outline: novelData.outline,
      generatedContent: novelData.generatedContent,
      fullText: this.combineNovelContent(novelData.outline, novelData.generatedContent),
      wordCount: this.calculateWordCount(novelData.generatedContent),
      novelConfig: novelData.novelConfig || {}
    }

    const metadata = {
      progress: novelData.progress,
      totalSections: novelData.outline?.length || 0,
      completedSections: Object.keys(novelData.generatedContent || {}).length,
      novelConfig: novelData.novelConfig || {},
      genre: novelData.novelConfig?.genre || '',
      style: novelData.novelConfig?.style || '',
      plotStructure: novelData.novelConfig?.plotStructure || '',
      language: novelData.novelConfig?.language || '',
      generatedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: novelData.query,
      agentType: AGENT_TYPES.NOVEL,
      content: content,
      sources: [], // Novel doesn't use external sources
      metadata: metadata,
      status: novelData.status || 'completed',
      historyId: novelData.historyId
    })
  }

  /**
   * Menyimpan hasil Search Agent
   * @param {Object} searchData - Data hasil pencarian
   * @param {string} searchData.query - Query pencarian asli
   * @param {string} searchData.answer - Jawaban yang dihasilkan
   * @param {Array} searchData.sources - Sumber referensi
   * @param {Array} searchData.images - Gambar dari hasil pencarian
   * @param {string} searchData.searchType - Tipe pencarian (web/academic)
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveSearchResult(searchData) {
    const content = {
      answer: searchData.answer,
      searchType: searchData.searchType,
      images: searchData.images || [],
      wordCount: this.calculateWordCount(searchData.answer)
    }

    const metadata = {
      searchType: searchData.searchType,
      sourceCount: searchData.sources?.length || 0,
      imageCount: searchData.images?.length || 0,
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: searchData.query,
      agentType: AGENT_TYPES.SEARCH,
      content: content,
      sources: searchData.sources || [],
      metadata: metadata
    })
  }

  static async saveTrendingResult(trendingData) {
    console.log('🔥 saveTrendingResult called with:', {
      query: trendingData.query,
      agentType: AGENT_TYPES.TRENDING,
      agentTypeValue: AGENT_TYPES.TRENDING
    })

    const content = {
      answer: trendingData.answer,
      trendingItems: trendingData.trendingItems || [],
      images: trendingData.images || [],
      wordCount: this.calculateWordCount(trendingData.answer),
      dateRange: trendingData.dateRange,
      region: trendingData.region
    }

    const metadata = {
      sourceCount: trendingData.sources?.length || 0,
      imageCount: trendingData.images?.length || 0,
      trendingItemsCount: trendingData.trendingItems?.length || 0,
      dateRange: trendingData.dateRange,
      region: trendingData.region,
      enhancedWithLLM: trendingData.enhancedWithLLM || false,
      generatedAt: new Date().toISOString()
    }

    const agentData = {
      query: trendingData.query,
      agentType: AGENT_TYPES.TRENDING,
      content: content,
      sources: trendingData.sources || [],
      metadata: metadata
    }

    console.log('🔥 Calling saveAgentResult with agentType:', agentData.agentType)
    
    return this.saveAgentResult(agentData)
  }

  /**
   * Menyimpan hasil Writing Agent
   * @param {Object} writingData - Data writing
   * @param {string} writingData.query - Query asli
   * @param {string} writingData.title - Judul dokumen
   * @param {string} writingData.content - Konten dokumen
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveWritingResult(writingData) {
    const content = {
      title: writingData.title,
      content: writingData.content,
      wordCount: this.calculateWordCount(writingData.content),
      characterCount: writingData.content?.length || 0
    }

    const metadata = {
      wordCount: this.calculateWordCount(writingData.content),
      characterCount: writingData.content?.length || 0,
      estimatedPages: Math.ceil(this.calculateWordCount(writingData.content) / 500),
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: writingData.query,
      agentType: AGENT_TYPES.WRITING,
      content: content,
      sources: [],
      metadata: metadata,
      title: writingData.title
    })
  }

  /**
   * Menyimpan hasil PPT Agent
   * @param {Object} pptData - Data PPT
   * @param {string} pptData.query - Query asli
   * @param {Array} pptData.outline - Outline presentasi
   * @param {Object} pptData.generatedContent - Konten slide yang dihasilkan
   * @param {Array} pptData.academicSources - Sumber akademik
   * @param {Object} pptData.progress - Progress generation
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async savePPTResult(pptData) {
    const content = {
      outline: pptData.outline || [],
      generatedContent: pptData.generatedContent || {},
      slideCount: pptData.outline?.length || 0,
      totalElements: Object.values(pptData.generatedContent || {}).reduce((total, slide) => 
        total + (slide.visualElements?.length || 0), 0
      ),
      fullContent: this.combinePPTContent(pptData.outline, pptData.generatedContent)
    }

    const metadata = {
      slideCount: pptData.outline?.length || 0,
      totalElements: Object.values(pptData.generatedContent || {}).reduce((total, slide) => 
        total + (slide.visualElements?.length || 0), 0
      ),
      sourceCount: pptData.academicSources?.length || 0,
      progress: pptData.progress || { current: 0, total: 0 },
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: pptData.query,
      agentType: AGENT_TYPES.PPT,
      content: content,
      sources: pptData.academicSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Chart Agent
   * @param {Object} chartData - Data Chart
   * @param {string} chartData.query - Query asli
   * @param {string} chartData.inputType - Tipe input ('topic' atau 'data')
   * @param {any} chartData.inputData - Data input
   * @param {Array} chartData.generatedCharts - Chart yang dihasilkan
   * @param {Array} chartData.searchResults - Hasil pencarian untuk referensi
   * @param {Object} chartData.progress - Progress generation
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveChartResult(chartData) {
    const content = {
      inputType: chartData.inputType || 'topic',
      inputData: chartData.inputData,
      generatedCharts: chartData.generatedCharts || [],
      chartCount: chartData.generatedCharts?.length || 0,
      chartTypes: chartData.generatedCharts?.map(chart => chart.type) || [],
      searchResults: chartData.searchResults || [] // Store search results in content
    }

    const metadata = {
      inputType: chartData.inputType || 'topic',
      chartCount: chartData.generatedCharts?.length || 0,
      chartTypes: chartData.generatedCharts?.map(chart => chart.type) || [],
      progress: chartData.progress || { current: 0, total: 0 },
      generatedAt: new Date().toISOString(),
      searchResultsCount: chartData.searchResults?.length || 0
    }

    return this.saveAgentResult({
      query: chartData.query,
      agentType: AGENT_TYPES.CHART,
      content: content,
      sources: chartData.searchResults || [], // Also store in sources for compatibility
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Research Agent
   * @param {Object} researchData - Data research paper
   * @param {string} researchData.query - Query asli
   * @param {Object} researchData.researchPaper - Research paper yang dihasilkan
   * @param {Array} researchData.academicSources - Sumber akademik
   * @param {Array} researchData.images - Gambar dari hasil pencarian
   * @param {Object} researchData.metadata - Metadata tambahan
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveResearchResult(researchData) {
    const content = {
      researchPaper: researchData.researchPaper || {},
      images: researchData.images || [],
      wordCount: this.calculateResearchWordCount(researchData.researchPaper),
      sectionCount: this.calculateResearchSectionCount(researchData.researchPaper),
      chartCount: this.calculateResearchChartCount(researchData.researchPaper)
    }

    const metadata = {
      topic: researchData.query,
      wordCount: this.calculateResearchWordCount(researchData.researchPaper),
      sectionCount: this.calculateResearchSectionCount(researchData.researchPaper),
      chartCount: this.calculateResearchChartCount(researchData.researchPaper),
      sourceCount: researchData.academicSources?.length || 0,
      imageCount: researchData.images?.length || 0,
      creditsUsed: 30,
      generatedAt: new Date().toISOString(),
      ...researchData.metadata
    }

    return this.saveAgentResult({
      query: researchData.query,
      agentType: AGENT_TYPES.RESEARCH,
      content: content,
      sources: researchData.academicSources || [],
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil Video Agent
   * @param {Object} videoData - Data video
   * @param {string} videoData.query - Query asli
   * @param {Array} videoData.videos - Array video yang dihasilkan
   * @param {Object} videoData.settings - Settings yang digunakan
   * @param {string} videoData.videoType - Tipe video (text_to_video, image_to_video)
   * @param {string} videoData.aspectRatio - Aspect ratio video
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveVideoResult(videoData) {
    const content = {
      videos: videoData.videos || [],
      totalVideos: videoData.videos?.length || 0,
      settings: videoData.settings || {},
      videoType: videoData.videoType || 'text_to_video',
      aspectRatio: videoData.aspectRatio || '16:9'
    }

    const metadata = {
      videoCount: videoData.videos?.length || 0,
      settings: videoData.settings || {},
      videoType: videoData.videoType || 'text_to_video',
      aspectRatio: videoData.aspectRatio || '16:9',
      creditsUsed: 20, // Video generation typically uses 20 credits
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: videoData.query,
      agentType: AGENT_TYPES.VIDEO,
      content: content,
      sources: [], // Videos don't typically have sources
      metadata: metadata
    })
  }

  /**
   * Menyimpan hasil PDF Agent
   * @param {Object} pdfData - Data PDF analysis
   * @param {string} pdfData.query - Query asli
   * @param {string} pdfData.fileName - Nama file PDF
   * @param {number} pdfData.fileSize - Ukuran file PDF
   * @param {string} pdfData.analysis - Hasil analisis PDF
   * @param {number} pdfData.extractedTextLength - Panjang teks yang diekstrak
   * @param {string} pdfData.mode - Mode LLM yang digunakan
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async savePDFResult(pdfData) {
    const content = {
      fileName: pdfData.fileName,
      fileSize: pdfData.fileSize,
      analysis: pdfData.analysis,
      extractedTextLength: pdfData.extractedTextLength,
      mode: pdfData.mode
    }

    const metadata = {
      fileName: pdfData.fileName,
      fileSize: pdfData.fileSize,
      extractedTextLength: pdfData.extractedTextLength,
      mode: pdfData.mode,
      creditsUsed: 10, // PDF analysis uses 10 credits
      generatedAt: new Date().toISOString()
    }

    return this.saveAgentResult({
      query: pdfData.query,
      agentType: 'pdf_agent',
      content: content,
      sources: [], // PDF analysis doesn't have external sources
      metadata: metadata
    })
  }

  // Helper methods
  static generateTitle(query, agentType) {
    const typeNames = {
      [AGENT_TYPES.SKRIPSI]: 'Skripsi',
      [AGENT_TYPES.ARTIKEL]: 'Artikel',
      [AGENT_TYPES.MAKALAH]: 'Makalah',
      [AGENT_TYPES.GAMBAR]: 'Gambar',
      [AGENT_TYPES.VIDEO]: 'Video',
      [AGENT_TYPES.BERITA]: 'Berita',
      [AGENT_TYPES.BUKU]: 'Buku',
      [AGENT_TYPES.SEARCH]: 'Search',
      [AGENT_TYPES.TRENDING]: 'Trending',
      [AGENT_TYPES.WRITING]: 'Writing',
      [AGENT_TYPES.PPT]: 'PPT',
      [AGENT_TYPES.CHART]: 'Chart',
      [AGENT_TYPES.RESEARCH]: 'Research',
      [AGENT_TYPES.PDF]: 'PDF'
    }
    
    const typeName = typeNames[agentType] || 'Agent'
    const truncatedQuery = query.length > 50 ? query.substring(0, 50) + '...' : query
    return `${typeName}: ${truncatedQuery}`
  }

  static calculateWordCount(content) {
    if (typeof content === 'string') {
      return content.split(/\s+/).filter(word => word.length > 0).length
    }
    
    if (typeof content === 'object' && content !== null) {
      let totalWords = 0
      Object.values(content).forEach(value => {
        if (typeof value === 'string') {
          totalWords += value.split(/\s+/).filter(word => word.length > 0).length
        }
      })
      return totalWords
    }
    
    return 0
  }

  static calculateBeritaWordCount(content) {
    if (typeof content === 'string') {
      return content.split(/\s+/).filter(word => word.length > 0).length
    }
    return 0
  }

  static combineSkripsiContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(section => {
      const content = generatedContent[section.id] || ''
      return `## ${section.title}\n\n${content}`
    }).join('\n\n')
  }

  static combineArtikelContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(section => {
      const content = generatedContent[section.id] || ''
      return `## ${section.title}\n\n${content}`
    }).join('\n\n')
  }

  static combineMakalahContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(section => {
      const content = generatedContent[section.id] || ''
      return `## ${section.title}\n\n${content}`
    }).join('\n\n')
  }

  static combineBukuContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(section => {
      const content = generatedContent[section.id] || ''
      return `## ${section.title}\n\n${content}`
    }).join('\n\n')
  }

  static combineNovelContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(section => {
      const content = generatedContent[section.id] || ''
      return `## ${section.title}\n\n${content}`
    }).join('\n\n')
  }

  static combinePPTContent(outline, generatedContent) {
    if (!outline || !generatedContent) return ''
    
    return outline.map(slide => {
      const slideContent = generatedContent[slide.id] || {}
      const content = slideContent.content || {}
      const mainPoints = content.mainPoints || []
      const details = content.details || ''
      const speakerNotes = slideContent.speakerNotes || ''
      
      let slideText = `## ${slide.title}\n\n`
      
      if (details) {
        slideText += `${details}\n\n`
      }
      
      if (mainPoints.length > 0) {
        slideText += 'Poin Utama:\n'
        slideText += mainPoints.map(point => `• ${point}`).join('\n')
        slideText += '\n\n'
      }
      
      if (speakerNotes) {
        slideText += `Catatan Presenter: ${speakerNotes}\n\n`
      }
      
      return slideText
    }).join('\n\n')
  }

  // Helper methods untuk Research Agent
  static calculateResearchWordCount(researchPaper) {
    if (!researchPaper || typeof researchPaper !== 'object') return 0
    
    let totalWords = 0
    
    // Count words in all sections
    Object.values(researchPaper).forEach(section => {
      if (typeof section === 'object' && section !== null) {
        Object.values(section).forEach(content => {
          if (typeof content === 'string') {
            totalWords += content.split(/\s+/).filter(word => word.length > 0).length
          } else if (Array.isArray(content)) {
            content.forEach(item => {
              if (typeof item === 'string') {
                totalWords += item.split(/\s+/).filter(word => word.length > 0).length
              }
            })
          }
        })
      } else if (typeof section === 'string') {
        totalWords += section.split(/\s+/).filter(word => word.length > 0).length
      }
    })
    
    return totalWords
  }

  static calculateResearchSectionCount(researchPaper) {
    if (!researchPaper || typeof researchPaper !== 'object') return 0
    return Object.keys(researchPaper).length
  }

  static calculateResearchChartCount(researchPaper) {
    if (!researchPaper || !researchPaper.results || !researchPaper.results.dataVisualizations) return 0
    return researchPaper.results.dataVisualizations.length || 0
  }
}

export default AgentHistoryIntegration